/**
 * Ambience.js — the free 30% of the atmosphere.
 * owner: fx
 *
 *  - Volumetric light shafts: analytic ray/cylinder integration on a proxy mesh,
 *    depth-clamped so the shaft is occluded by geometry and never shows an
 *    intersection line. Not a stack of billboards.
 *  - Dust motes drifting inside the shafts and around the camera, lit by the sun
 *    so they glitter when backlit.
 *  - Wind-blown grit and drifting embers.
 *  - Heat shimmer: emits a haze particle field; the screen-space refraction is
 *    published for the render team (see RENDER_HANDOFF.md).
 */
import * as THREE from 'three';
import { NOISE, DEPTH_HELPERS } from './Shaders.js';

const SHAFT_VS = /* glsl */`
varying vec3 vWorld;
void main(){
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xyz;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const SHAFT_FS = /* glsl */`
precision highp float;
${NOISE}
${DEPTH_HELPERS}
uniform sampler2D tBlue;
uniform vec2 uResolution;
uniform vec3 uBase, uAxis, uColor, uCamFwd;
uniform float uRadius, uHeight, uDensity, uTime, uHasDepth, uEndTaper;
varying vec3 vWorld;

void main(){
  vec3 ro = cameraPosition;
  vec3 rd = normalize(vWorld - ro);

  vec3 oc = ro - uBase;
  vec3 d = rd - uAxis * dot(rd, uAxis);
  vec3 o = oc - uAxis * dot(oc, uAxis);
  float a = dot(d, d);
  float b = 2.0 * dot(d, o);
  float c = dot(o, o) - uRadius * uRadius;
  if (a < 1e-8) discard;
  float disc = b * b - 4.0 * a * c;
  if (disc <= 0.0) discard;
  float sq = sqrt(disc);
  float t0 = (-b - sq) / (2.0 * a);
  float t1 = (-b + sq) / (2.0 * a);

  float ha = dot(rd, uAxis);
  float hb = dot(oc, uAxis);
  if (abs(ha) > 1e-5){
    float ta = (0.0 - hb) / ha;
    float tb = (uHeight - hb) / ha;
    t0 = max(t0, min(ta, tb));
    t1 = min(t1, max(ta, tb));
  } else if (hb < 0.0 || hb > uHeight) discard;

  t0 = max(t0, 0.0);

  if (uHasDepth > 0.5){
    float sceneZ = fxSceneZ(gl_FragCoord.xy / uResolution);
    if (sceneZ < 1.0e5){
      float cosang = max(0.05, dot(rd, uCamFwd));
      t1 = min(t1, sceneZ / cosang);
    }
  }
  if (t1 <= t0) discard;

  float jitter = texture2D(tBlue, gl_FragCoord.xy / 256.0).r;
  float span = t1 - t0;
  float acc = 0.0;
  for (int i = 0; i < 5; i++){
    float f = (float(i) + jitter) / 5.0;
    vec3 p = ro + rd * (t0 + span * f);
    vec3 rel = p - uBase;
    float h = dot(rel, uAxis);
    vec3 perp = rel - uAxis * h;
    float rr = clamp(length(perp) / uRadius, 0.0, 1.0);
    float radial = pow(1.0 - rr, 2.2);
    float hn = clamp(h / uHeight, 0.0, 1.0);
    float taper = mix(1.0, uEndTaper, hn);
    float n = fbm3(p * 1.35 + vec3(0.0, -uTime * 0.09, uTime * 0.04), 3, 0.55);
    acc += radial * taper * (0.42 + 1.05 * n);
  }
  acc *= span / 5.0 * uDensity;

  float alpha = clamp(acc, 0.0, 1.0);
  if (alpha <= 0.002) discard;
  vec4 outColor = vec4(uColor * acc, alpha);
  #if defined( TONE_MAPPING )
    outColor.rgb = toneMapping(outColor.rgb);
  #endif
  outColor = linearToOutputTexel(outColor);
  gl_FragColor = outColor;
}
`;

export class LightShaft {
  /** @param {object} o { base:Vec3, dir:Vec3, radius, height, color:[r,g,b], density, endTaper } */
  constructor(o = {}) {
    const radius = o.radius ?? 1.2;
    const height = o.height ?? 6;
    const geo = new THREE.CylinderGeometry(radius * 1.08, radius * 1.08, height * 1.02, 14, 1, true);
    geo.translate(0, height * 0.5, 0);
    this.uniforms = {
      tDepth: { value: null }, tNormal: { value: null }, uDepthMode: { value: 0 },
      tBlue: { value: null },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uBase: { value: new THREE.Vector3() },
      uAxis: { value: new THREE.Vector3(0, 1, 0) },
      uColor: { value: new THREE.Color(1, 0.92, 0.78) },
      uCamFwd: { value: new THREE.Vector3(0, 0, -1) },
      uRadius: { value: radius },
      uHeight: { value: height },
      uDensity: { value: o.density ?? 0.16 },
      uTime: { value: 0 },
      uHasDepth: { value: 0 },
      uEndTaper: { value: o.endTaper ?? 0.45 },
    };
    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: SHAFT_VS,
      fragmentShader: SHAFT_FS,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2900;
    this.mesh.name = 'fx_lightshaft';
    this.mesh.userData.noCollide = true;
    this.mesh.userData.fx = true;
    this.radius = radius;
    this.height = height;
    this.base = new THREE.Vector3();
    this.axis = new THREE.Vector3(0, 1, 0);
    if (o.base) this.setPlacement(o.base, o.dir || new THREE.Vector3(0, -1, 0));
    if (o.color) this.uniforms.uColor.value.setRGB(o.color[0], o.color[1], o.color[2]);
  }

  /** @param {THREE.Vector3} top the mouth of the shaft (window / hole) @param {THREE.Vector3} dir direction it travels */
  setPlacement(top, dir) {
    const d = _v.copy(dir).normalize();
    this.base.copy(top);
    this.axis.copy(d);
    this.uniforms.uBase.value.copy(top);
    this.uniforms.uAxis.value.copy(d);
    // orient the proxy cylinder (local +Y) along the axis
    this.mesh.quaternion.setFromUnitVectors(_up.set(0, 1, 0), d);
    this.mesh.position.copy(top);
    this.mesh.updateMatrixWorld(true);
  }

  update(env) {
    const u = this.uniforms;
    u.tDepth.value = env.depthTexture;
    u.tNormal.value = env.normalTexture || env.depthTexture;
    u.uDepthMode.value = env.depthMode;
    u.tBlue.value = env.blueNoise;
    u.uHasDepth.value = env.depthTexture ? 1 : 0;
    u.uResolution.value.copy(env.resolution);
    u.uTime.value = env.time;
    u.uCamFwd.value.copy(env.camForward);
  }

  dispose() { this.mesh.geometry.dispose(); this.mesh.material.dispose(); }
}

const _v = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
